import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"

interface BusinessImageGalleryProps {
  images: string[]
  businessTitle: string
  onImageClick: (index: number) => void
  className?: string
}

export function BusinessImageGallery({ 
  images, 
  businessTitle, 
  onImageClick, 
  className 
}: BusinessImageGalleryProps) {
  const [currentIndex, setCurrentIndex] = React.useState(0)
  const [isScrolling, setIsScrolling] = React.useState(false)
  const scrollContainerRef = React.useRef<HTMLDivElement>(null)

  const scrollToIndex = (index: number) => {
    if (scrollContainerRef.current && !isScrolling) {
      setIsScrolling(true)
      const container = scrollContainerRef.current
      const imageWidth = container.clientWidth
      
      container.scrollTo({
        left: index * imageWidth,
        behavior: 'smooth'
      })
      
      setCurrentIndex(index)
      setTimeout(() => setIsScrolling(false), 300)
    }
  }

  const handleScroll = React.useCallback(() => {
    if (scrollContainerRef.current && !isScrolling) {
      const container = scrollContainerRef.current
      const imageWidth = container.clientWidth
      const newIndex = Math.round(container.scrollLeft / imageWidth)
      
      if (newIndex !== currentIndex) {
        setCurrentIndex(newIndex)
      }
    }
  }, [currentIndex, isScrolling])

  React.useEffect(() => {
    const container = scrollContainerRef.current
    if (container) {
      container.addEventListener('scroll', handleScroll)
      return () => container.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll])

  const goToPrevious = () => {
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : images.length - 1
    scrollToIndex(prevIndex)
  }

  const goToNext = () => {
    const nextIndex = currentIndex < images.length - 1 ? currentIndex + 1 : 0
    scrollToIndex(nextIndex)
  }

  if (images.length === 0) {
    return null
  }

  return (
    <div className={cn("relative group", className)}>
      {/* Main Gallery Container */}
      <div className="relative overflow-hidden rounded-xl bg-gray-100">
        {/* Scrollable Container */}
        <div
          ref={scrollContainerRef}
          className="flex overflow-x-auto scrollbar-hide snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {images.map((image, index) => (
            <div
              key={index}
              className="w-full flex-shrink-0 snap-center aspect-square md:aspect-[4/3] relative"
            >
              <img
                src={image}
                alt={`${businessTitle} work sample ${index + 1}`}
                className="w-full h-full object-cover cursor-pointer transition-transform duration-300 hover:scale-105"
                onClick={() => onImageClick(index)}
              />
              
              {/* Image overlay on hover */}
              <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors duration-300 cursor-pointer" 
                   onClick={() => onImageClick(index)} />
            </div>
          ))}
        </div>

        {/* Navigation Arrows - Only show if more than 1 image */}
        {images.length > 1 && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-white/80 hover:bg-white/90 shadow-lg backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              onClick={goToPrevious}
            >
              <ChevronLeft className="h-4 w-4 text-gray-700" />
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-white/80 hover:bg-white/90 shadow-lg backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              onClick={goToNext}
            >
              <ChevronRight className="h-4 w-4 text-gray-700" />
            </Button>
          </>
        )}

        {/* Image Counter - Only show if more than 1 image */}
        {images.length > 1 && (
          <div className="absolute top-3 right-3 bg-black/50 text-white px-2 py-1 rounded-full text-xs font-medium backdrop-blur-sm">
            {currentIndex + 1} / {images.length}
          </div>
        )}
      </div>

      {/* Dot Indicators - Only show if more than 1 image */}
      {images.length > 1 && (
        <div className="flex justify-center mt-4 space-x-2">
          {images.map((_, index) => (
            <button
              key={index}
              className={cn(
                "w-2 h-2 rounded-full transition-all duration-300",
                index === currentIndex 
                  ? "bg-blue-600 w-6" 
                  : "bg-gray-300 hover:bg-gray-400"
              )}
              onClick={() => scrollToIndex(index)}
            />
          ))}
        </div>
      )}

      {/* Scroll Hint for Mobile - Only show if more than 1 image */}
      {images.length > 1 && (
        <div className="md:hidden flex justify-center mt-2 text-xs text-gray-500">
          Swipe to see more images
        </div>
      )}
    </div>
  )
}